/**
 * Constantes de Scheduling/Agenda
 * 
 * Migrado desde:
 * - WidgetsNamesConstants.properties (mensajes de UI)
 * - Constants.java (constantes generales)
 * - ConstantsEJB.java (constantes de negocio)
 * 
 * NOTA: Las constantes de negocio (estados, tipos de cita) están en constants-ejb.ts
 * Este archivo solo contiene textos de UI y configuraciones específicas de agenda.
 * 
 * @see 580-Girop-ml/PrestacionServiciosCGR/src/com/sura/prestacionservicioscgr/shared/constants/scheduling/WidgetsNamesConstants.properties
 */

import { ESTADO_CITA, TIPO_CITA, CATALOGO_GRUPOS_ESTUDIO, CATALOGO_TIPOS_FORMACION, CATALOGO_TIPOS_INVESTIGACION } from './constants-ejb';

// Re-exportar constantes de negocio para compatibilidad
export { ESTADO_CITA, TIPO_CITA, CATALOGO_GRUPOS_ESTUDIO, CATALOGO_TIPOS_FORMACION, CATALOGO_TIPOS_INVESTIGACION } from './constants-ejb';

// Alias para compatibilidad con código existente
export const TIPOS_CITA_IDS = {
  // === Tipos básicos (de constants-ejb) ===
  CAMPO: TIPO_CITA.CAMPO,
  INFORME: TIPO_CITA.INFORME,
  CAMPO_COMPLEMENTARIO: TIPO_CITA.CAMPO_COMPLEMENTARIO,
  INFORME_COMPLEMENTARIO: TIPO_CITA.INFORME_COMPLEMENTARIO,
  
  // === Tipos adicionales específicos de agenda ===
  PROGRAMACION_EN_CLIENTE: 50,
  SEGUIMIENTO_INTEGRAL_CLIENTE: 25,
  DIRECCION_ERRADA_EMPRESA: 26,
  GRUPO_ESTUDIO: 12,
  FORMACION_TECNICA: 15,
  INVESTIGACION_APLICADA: 52,
  CONTENIDOS_TECNICOS: 54,
  CITA_TENTATIVA: 20,
  TUTORIA_PEC_PAI: 40,
  TUTORIA_CAMPO: 41,
  SITUACION_INESPERADA: 27,
  AUTOAGENDAMIENTO_WHATSAPP: 56,
  UNIDAD_APRENDIZAJE_SURA: 53,
  DESARROLLO_EQUIPOS: 46
} as const;

// ============================================================================
// VISIBILIDAD DE CONTACTOS Y ENLACE TEAMS POR TIPO DE CITA
// Legacy: CrearCitaAdministrativaViewImpl.onListBoxTypeChange (L1526-1555) y
// ServiceAppointmentInterfaceImpl.onListBoxTypeChange/setVisibleMode (L1177-1184, L1879).
// ============================================================================

/** Legacy CrearCitaAdministrativaViewImpl.reset (L1972): DSUSO_CITA de los tipos de Línea AT. */
export const USO_CITA_LINEA_AT = 'LINEAAT';

/** Tipos que muestran Contactos sin importar Enlace Teams (legacy L1553-1555: CAMPO). */
export const TIPOS_CITA_CONTACTOS_SIEMPRE_VISIBLES: readonly number[] = [
  TIPOS_CITA_IDS.CAMPO
];

/** Tipos que ocultan Contactos y Enlace Teams; Teams se envía siempre 'N' (legacy L1879, HU-175). */
export const TIPOS_CITA_SIN_CONTACTOS_NI_TEAMS: readonly number[] = [
  TIPOS_CITA_IDS.INFORME
];

// ============================================================================
// TOOLTIPS - Migrado de WidgetsNamesConstants.properties
// ============================================================================

export const SCHEDULING_TOOLTIPS = {
  /** toolTipCalendarClickOverToCreateAppointment */
  CALENDAR_CLICK_TO_CREATE: 'Haga click sobre la hora en la cual desee crear una nueva cita',
  
  /** toolTipCalendarClickOverToEditAppointment */
  CALENDAR_CLICK_TO_EDIT: 'Haga click sobre la cita para modificarla',
  
  /** toolTipSelectADay */
  SELECT_A_DAY: 'Seleccione un dia del calendario',
  
  /** toolTipAppointmentDate */
  APPOINTMENT_DATE: 'Ingrese la fecha de programación de la cita',
  
  /** toolTipAppointmentHour */
  APPOINTMENT_HOUR: 'Ingrese la hora en la cual se comenzará la cita',
  
  /** toolTipAppointmentDuration */
  APPOINTMENT_DURATION: 'Ingrese la duración de la cita',
  
  /** toolTipAppointmentQuantity */
  APPOINTMENT_QUANTITY: 'Ingrese la cantidad a programar en la entrega del servicio',
  
  /** toolTipAppointmentPeriodicity */
  APPOINTMENT_PERIODICITY: 'Seleccione la periodicidad de la lista',
  
  /** toolTipAppointmentRepetitions */
  APPOINTMENT_REPETITIONS: 'Ingrese el número de veces que se repetirá la cita',
  
  /** toolTipAppointmentType */
  APPOINTMENT_TYPE: 'Seleccione el tipo de cita de la lista',
  
  /** toolTipAppointmentNotes */
  APPOINTMENT_NOTES: 'Ingrese las observaciones que considere necesarias',
  
  /** toolTipCancelAppointmentWho */
  CANCEL_APPOINTMENT_WHO: 'Seleccione el rol de quien realiza la cancelación de la entrega',
  
  /** toolTipCancelAppointmentContactName */
  CANCEL_APPOINTMENT_CONTACT: 'Ingrese el nombre del contacto que ha realizado la cancelación',
  
  /** toolTipCancelAppointmentNotes */
  CANCEL_APPOINTMENT_NOTES: 'Ingrese las observaciones que considere necesarias'
} as const;

// ============================================================================
// LABELS - Migrado de WidgetsNamesConstants.properties
// ============================================================================

export const SCHEDULING_LABELS = {
  /** scheduleAdvisorTitle */
  SCHEDULE_ADVISOR_TITLE: 'Programación de consultores para el servicio',
  
  /** appointmentDialogTitle */
  APPOINTMENT_DIALOG_TITLE: 'Programación de la cita',
  
  /** appointmentDialogSubtitle */
  APPOINTMENT_DIALOG_SUBTITLE: 'Datos de la cita',
  
  /** cancelAppointmentDialogTitle */
  CANCEL_APPOINTMENT_TITLE: 'Cancelar programación',
  
  /** cancelAppointmentDialogSubtitle */
  CANCEL_APPOINTMENT_SUBTITLE: 'Datos de la cancelación',
  
  /** appointmentType */
  APPOINTMENT_TYPE: 'Tipo de cita',
  
  /** appointmentTypeField */
  APPOINTMENT_TYPE_FIELD: 'Campo',
  
  /** appointmentTypeReport */
  APPOINTMENT_TYPE_REPORT: 'Informe',
  
  /** appointmentDate */
  APPOINTMENT_DATE: 'Fecha cita (AAAA/MM/DD)',
  
  /** appointmentHour */
  APPOINTMENT_HOUR: 'Hora de inicio (HH:MM)',
  
  /** appointmentDuration */
  APPOINTMENT_DURATION: 'Duración',
  
  /** appointmentScheduledQuantity */
  APPOINTMENT_SCHEDULED_QUANTITY: 'Cantidad programada',
  
  /** appointmentPeriodicityLabel */
  APPOINTMENT_PERIODICITY: 'Periodicidad',
  
  /** appointmentRepetitions */
  APPOINTMENT_REPETITIONS: 'Repeticiones',
  
  /** appointmentPeriodicityEndingDate */
  APPOINTMENT_PERIODICITY_END_DATE: 'Fecha final',
  
  /** appointmentTotalScheduledQuantity */
  APPOINTMENT_TOTAL_QUANTITY: 'Cantidad programada total',
  
  /** appointmentStatus */
  APPOINTMENT_STATUS: 'Estado de la cita',
  
  /** contactLabel */
  CONTACT: 'Contacto',
  
  /** advisorsList */
  ADVISORS_LIST: 'Lista de consultores',
  
  /** deleteApointment */
  DELETE_APPOINTMENT: 'Cancelar programación',
  
  /** appointmentAcomplishment */
  APPOINTMENT_ACCOMPLISHMENT: 'Ingresar cumplimiento',
  
  /** Administrativas - del selector tipo cita */
  ADMINISTRATIVE_APPOINTMENTS: 'Administrativas',
  
  /** Campo/Informe - del selector tipo cita */
  FIELD_REPORT: 'Campo/Informe'
} as const;

// ============================================================================
// MENSAJES - Migrado de WidgetsNamesConstants.properties
// ============================================================================

export const SCHEDULING_MESSAGES = {
  /** noSchedulesForAdvisorAndService */
  NO_APPOINTMENTS_FOR_WEEK: 'Aún no hay citas programadas para este consultor en la semana seleccionada',
  
  /** noSchedulesWeek */
  NO_SCHEDULES_WEEK: 'Semana sin citas',
  
  /** noSchedulesForThisService */
  NO_SCHEDULES_FOR_SERVICE: 'No hay citas programadas para este servicio',
  
  /** successfulCancelled */
  APPOINTMENT_CANCELLED: 'La cita se ha cancelado correctamente',
  
  /** messageAppointmentNotEditable */
  APPOINTMENT_NOT_EDITABLE: 'Por favor tenga en cuenta que el estado actual de esta cita no permite realizar ninguna modificación.',
  
  /** appointmentStatusNotFulfillable */
  APPOINTMENT_NOT_FULFILLABLE: 'El estado actual de la cita no permite el ingreso de cumplimientos.',
  
  /** appointmentStatusNotCancelable */
  APPOINTMENT_NOT_CANCELABLE: 'El estado actual de la cita no permite la cancelación.',
  
  /** confidentialAppointmentMessage */
  CONFIDENTIAL_APPOINTMENT: 'Esta cita es confidencial y sólo podrá ser vista por el consultor asignado a la misma.',
  
  /** appointmentWithPeriodicityMessagePart1 + Part2 */
  APPOINTMENT_WITH_PERIODICITY: 'Esta cita se encuentra asociada a una programación periódica. ¿Desea modificar todas las citas futuras asociadas que sean actualizables?',
  
  /** Mensajes de éxito personalizados */
  APPOINTMENT_CREATED: 'Cita creada correctamente',
  APPOINTMENT_UPDATED: 'Cita actualizada correctamente',
  APPOINTMENT_COMPLETED: 'Cita cumplida correctamente',
  
  /** Mensajes de validación */
  SELECT_ADVISOR_FIRST: 'Debe seleccionar un consultor primero',
  
  /** Mensaje temporal para funcionalidad en desarrollo */
  FEATURE_COMING_SOON: 'La creación de citas de servicio estará disponible próximamente',
  
  /** loadingCalendarData */
  LOADING_CALENDAR: 'Cargando datos de calendario...',
  
  /** noAdvisorAvailabilityInfo */
  NO_ADVISOR_AVAILABILITY: 'No se encontró información de disponibilidad para el consultor seleccionado',
  
  /** scheduleAdvisorNoAddress */
  NO_ADDRESSES_TO_SHOW: 'No hay direcciones para mostrar, por favor seleccione un dia en el calendario que contenga citas con direcciones de entrega.'
} as const;

// ============================================================================
// PERIODICIDADES - Migrado de WidgetsNamesConstants.properties
// ============================================================================

export const PERIODICIDADES = {
  /** appointmentPeriodicityOptionDaily */
  DAILY: { value: 'DIARIO', label: 'Diario' },
  /** appointmentPeriodicityOptionWeekly */
  WEEKLY: { value: 'SEMANAL', label: 'Semanal' },
  /** appointmentPeriodicityOptionMonthly */
  MONTHLY: { value: 'MENSUAL', label: 'Mensual' },
  /** appointmentPeriodicityOptionYearly */
  YEARLY: { value: 'ANUAL', label: 'Anual' }
} as const;

// ============================================================================
// CONFIGURACIÓN DE CAMPOS POR TIPO DE CITA
// Migrado de CrearCitaAdministrativaViewImpl.onListBoxTypeChange()
// ============================================================================

/**
 * Configuración de campos visibles por tipo de cita.
 * Migrado de CrearCitaAdministrativaViewImpl.onListBoxTypeChange()
 */
export const CAMPOS_POR_TIPO_CITA: Record<number, {
  beneficiario?: boolean;
  tarea?: boolean;
  centroTrabajo?: boolean;
  direccion?: boolean;
  contacto?: boolean;
  virtual?: boolean;
  grupoEstudio?: boolean;
  formacionTecnica?: boolean;
  investigacion?: boolean;
  unidadNegocio?: boolean;
  observacionObligatoria?: boolean;
  duracionFija?: number; // horas
  ocultarPeriodicidad?: boolean;
  ppr?: boolean; // Panel de PPR (Profesional)
}> = {
  // CAMPO (17): Contacto visible
  [TIPOS_CITA_IDS.CAMPO]: { contacto: true },
  
  // INFORME (18): Sin campos adicionales específicos
  [TIPOS_CITA_IDS.INFORME]: {},
  
  // CAMPO COMPLEMENTARIO (42): Beneficiario + Tarea + Centro de Trabajo
  // Legacy: Periodicidad SÍ se muestra (el usuario lo confirmó visualmente)
  [TIPOS_CITA_IDS.CAMPO_COMPLEMENTARIO]: { 
    beneficiario: true, 
    tarea: true, 
    centroTrabajo: true
  },
  
  // INFORME COMPLEMENTARIO (43): Beneficiario + Tarea + Centro de Trabajo + Contacto
  // Similar a CAMPO_COMPLEMENTARIO
  [TIPOS_CITA_IDS.INFORME_COMPLEMENTARIO]: { 
    beneficiario: true, 
    tarea: true, 
    centroTrabajo: true,
    contacto: true
  },
  
  // PROGRAMACION EN CLIENTE (50): Beneficiario + Contacto (requerido en producción)
  [TIPOS_CITA_IDS.PROGRAMACION_EN_CLIENTE]: { beneficiario: true, contacto: true },
  
  // SEGUIMIENTO INTEGRAL AL CLIENTE (25): Beneficiario + PPR + Observación obligatoria
  // También conocido como "En Función del Cliente Sin OC"
  [TIPOS_CITA_IDS.SEGUIMIENTO_INTEGRAL_CLIENTE]: { 
    beneficiario: true, 
    ppr: true, 
    observacionObligatoria: true 
  },
  
  // DIRECCION ERRADA EMPRESA (26): Beneficiario, duración 30min
  [TIPOS_CITA_IDS.DIRECCION_ERRADA_EMPRESA]: { beneficiario: true },
  
  // CITA TENTATIVA (20): Beneficiario + Tarea + Dirección
  [TIPOS_CITA_IDS.CITA_TENTATIVA]: { beneficiario: true, tarea: true, direccion: true },
  
  // SITUACION INESPERADA (27): Observaciones obligatorias
  [TIPOS_CITA_IDS.SITUACION_INESPERADA]: { observacionObligatoria: true },
  
  // TUTORIA PEC/PAI (40): Tarea + Virtual
  [TIPOS_CITA_IDS.TUTORIA_PEC_PAI]: { tarea: true, virtual: true },
  
  // TUTORIA CAMPO (41): Beneficiario + Tarea + Virtual
  [TIPOS_CITA_IDS.TUTORIA_CAMPO]: { beneficiario: true, tarea: true, virtual: true },
  
  // GRUPO DE ESTUDIO (12): Campos de grupo de estudio
  [TIPOS_CITA_IDS.GRUPO_ESTUDIO]: { grupoEstudio: true },
  
  // FORMACION TECNICA (15): Tipo de formación
  [TIPOS_CITA_IDS.FORMACION_TECNICA]: { formacionTecnica: true },
  
  // INVESTIGACION APLICADA (52): Tipo de investigación
  [TIPOS_CITA_IDS.INVESTIGACION_APLICADA]: { investigacion: true },
  
  // CONTENIDOS TECNICOS (54): Unidad de negocio
  [TIPOS_CITA_IDS.CONTENIDOS_TECNICOS]: { unidadNegocio: true },
  
  // AUTOAGENDAMIENTO WHATSAPP (56): Duración fija 2 horas
  [TIPOS_CITA_IDS.AUTOAGENDAMIENTO_WHATSAPP]: { duracionFija: 2 }
};

// ============================================================================
// ESTADOS DE CITA - Re-exportado de constants-ejb para compatibilidad
// ============================================================================

/** @deprecated Usar ESTADO_CITA de constants-ejb directamente */
export const ESTADOS_CITA = ESTADO_CITA;

// Arrays de estados usados para validaciones de UI.
// ESTADOS_EDITABLES / CANCELABLES / CUMPLIBLES fueron ELIMINADOS: la fuente de
// verdad son los flags SNCITA_MODIFICABLE / SNPERMITE_CANCELACION /
// SNPERMITE_CUMPLIMIENTO que expone el backend (TPSC_ESTADOS), replicando el
// legacy ServiceAppointmentInterfaceImpl.isEditable/Deletable/FulFillableAppointment().
// Solo permanece ESTADOS_EJECUTADOS porque no hay flag SN equivalente para "cita ejecutada".
export const ESTADOS_EJECUTADOS: string[] = [ESTADO_CITA.EJECUTADA, ESTADO_CITA.EJE, '0028', ESTADO_CITA.PARCIAL, ESTADO_CITA.PEJ];

// ============================================================================
// QUIEN CANCELA - Migrado de WidgetsNamesConstants.properties
// ============================================================================

export const CANCEL_APPOINTMENT_WHO = {
  /** optionListKeyCompany */
  COMPANY: { code: 'EMPR', label: 'Empresa' },
  /** optionListKeyScheduler */
  SCHEDULER: { code: 'PROG', label: 'Programador' },
  /** optionListKeyAdvisor */
  ADVISOR: { code: 'ASE', label: 'Consultor' },
  /** optionListKeyProfessional */
  PROFESSIONAL: { code: 'PPR', label: 'Profesional' }
} as const;

// ============================================================================
// CATALOGOS - Re-exportados de constants-ejb.ts (fuente centralizada)
// ============================================================================
// CATALOGO_GRUPOS_ESTUDIO, CATALOGO_TIPOS_FORMACION, CATALOGO_TIPOS_INVESTIGACION
// están definidos en constants-ejb.ts y re-exportados arriba
